/**
 * Nudges endpoints — Wave 4 proactive-layer inbox.
 *
 * Mirrors v1 ``ando-frontend/app/api/nudges.ts`` and the BE router at
 * ``ando-agent/src/api/v1/endpoints/nudges.py``.
 *
 *   GET    /api/v1/nudges                paginated inbox list (root)
 *   GET    /api/v1/nudges/unread-count   badge counter (header bell)
 *   POST   /api/v1/nudges/{id}/ack       mark a nudge as read
 *   POST   /api/v1/nudges/{id}/act       commit a primary action
 *   POST   /api/v1/nudges/{id}/dismiss   user dismissed
 *
 * Only the bell + inbox shapes are typed here; act/dismiss bodies stay
 * loose until the Wave-4 inbox panel lands.
 *
 * Contract note (May 31, 2026 — fixes the "49 unread but empty panel"
 * regression): the BE list endpoint is the ROOT path. The previous
 * client called ``/nudges/inbox`` which fell through to the
 * ``/nudges/{nudge_id}`` detail route on the BE — FastAPI tried to
 * coerce the literal ``"inbox"`` into a UUID and 500-ed every list
 * fetch, leaving the panel in its empty state even though the counter
 * was rendering the correct number.
 *
 * Field aliasing: the BE wire field is ``status`` (per the persisted
 * ``pending_nudges.status`` column), the FE has historically exposed
 * it as ``state``. Adapter inside :func:`listNudgeInbox` does the
 * rename in one place so every consumer stays on the v1-shaped surface.
 */

import { apiClient } from "../ApiClient.js";

export interface NudgeUnreadCount {
  /** Total nudges in ``pending`` + ``delivered`` state for the current user. */
  unread_count: number;
}

export async function getNudgeUnreadCount(): Promise<NudgeUnreadCount> {
  return apiClient.get<NudgeUnreadCount>("/nudges/unread-count");
}

// ── Inbox list (used by ``NudgeInboxPanel`` — wired Wave 4 L5) ────────────

export interface NudgeInboxItem {
  readonly id: string;
  readonly nudge_type: string;
  readonly urgency: "low" | "normal" | "high" | "urgent";
  readonly title: string;
  readonly body: string;
  readonly created_at: string;
  readonly state: "pending" | "delivered" | "acted" | "dismissed";
  readonly cta_label?: string | null;
  /** Free-form payload — feature-flagged shape, BE owns it. */
  readonly payload?: Record<string, unknown> | null;
  /**
   * When a deadline is involved, the BE date the user should act on.
   * Surfaced to drive date-bucketed clustering in the Work hub timeline.
   */
  readonly target_at?: string | null;
  /**
   * Clustering key — same value across nudges ⇒ FE renders ONE card with
   * "N fonti correlate" and an expand affordance, instead of stacking
   * near-identical pills. Computed at read-time by the BE via
   * ``src.services.memory.nudge_topic.compute_topic_key``.
   */
  readonly topic_key?: string;
}

export interface NudgeInboxResponse {
  readonly items: readonly NudgeInboxItem[];
  readonly total: number;
}

export interface ListNudgeInboxOptions {
  limit?: number;
  offset?: number;
  /** Filter by state — default ``["pending", "delivered"]`` (unread). */
  state?: ReadonlyArray<NudgeInboxItem["state"]>;
}

/** Default "unread inbox" filter — matches BE root-path default. */
const DEFAULT_UNREAD_STATES: ReadonlyArray<NudgeInboxItem["state"]> = [
  "pending",
  "delivered",
];

function isDefaultUnreadFilter(
  states: ReadonlyArray<NudgeInboxItem["state"]> | undefined,
): boolean {
  if (!states || states.length === 0) return true;
  if (states.length !== DEFAULT_UNREAD_STATES.length) return false;
  return DEFAULT_UNREAD_STATES.every((s) => states.includes(s));
}

/**
 * Raw BE row shape — uses ``status`` (db column name) instead of the
 * FE-facing ``state`` alias. Adapter below renames in one place.
 */
interface NudgeInboxItemWire
  extends Omit<NudgeInboxItem, "state"> {
  readonly status: NudgeInboxItem["state"];
}

interface NudgeListResponseWire {
  readonly items: ReadonlyArray<NudgeInboxItemWire>;
  /** BE wraps the unread badge in the list response too. */
  readonly total_unread: number;
}

export async function listNudgeInbox(
  opts: ListNudgeInboxOptions = {},
): Promise<NudgeInboxResponse> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.offset !== undefined) params.set("offset", String(opts.offset));
  // BE accepts a SINGLE ``status`` query param (not a multi-value
  // ``state``). The default "unread" set (pending + delivered) is what
  // the BE returns when no filter is sent — so callers asking for the
  // default just omit the param. Callers asking for one specific state
  // forward it; multi-state filters beyond the default are dropped (the
  // panel doesn't ship them today).
  if (opts.state && opts.state.length === 1) {
    const [only] = opts.state;
    if (only) params.set("status", only);
  }
  // Multi-state filters beyond the default are silently coerced to the
  // BE default — the BE supports a single ``status`` param today and
  // adding a multi-status arg is a Wave-4 follow-up. Default semantic
  // (pending + delivered) covers every current call site.
  const qs = params.toString();
  const raw = await apiClient.get<NudgeListResponseWire>(
    qs ? `/nudges?${qs}` : "/nudges",
  );

  return {
    items: raw.items.map((row) => {
      const { status, ...rest } = row;
      return { ...rest, state: status };
    }),
    total: raw.total_unread,
  };
}

export async function ackNudge(nudgeId: string): Promise<void> {
  await apiClient.post<void>(
    `/nudges/${encodeURIComponent(nudgeId)}/ack`,
    {},
  );
}

export async function dismissNudge(nudgeId: string): Promise<void> {
  await apiClient.post<void>(
    `/nudges/${encodeURIComponent(nudgeId)}/dismiss`,
    {},
  );
}
